import { Ref, ref } from '@vue/composition-api';
import { DialogPlugin, DialogInstance } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips, { MsgError } from '@/renderer/scripts/utils/tips';
import user from '@/renderer/scripts/utils/user';
import CustomDialog from '@/renderer/scripts/utils/CustomDialog';

type Handler = {
  creatingOrg: Ref<boolean>;
  register: () => Promise<SMH.OrgItem | null>;
  createOrgFn: (channelTag?: SMH.ChannelTag) => Promise<SMH.OrgItem | null>;
  cancelDeregister: (orgId: number) => Promise<boolean>;
  showCancelDeregiseterDialog: (orgId: number, editionFlag: string, callback: Function) => Promise<void>;
  showFreezeDialog: (editionFlag?: string) => void;
  showIPRestrictDialog: (isInvitation?: boolean) => void;
};
export default function registerHandler(): Handler {
  const creatingOrg = ref(false);
  const methods = {
    // 注册个人版
    async register() {
      let registerData = null;
      try {
        const { data } = await smh.userAPI.restorePersonalAccount();
        registerData = data;
        tips.success(i18n_t('注册成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return registerData;
    },
    // 取消注销
    async cancelDeregister(organizationId: number) {
      let cancelSuccess = false;
      try {
        await smh.userAPI.cancelDeregister({
          organizationId,
        });
        cancelSuccess = true;
        tips.success(i18n_t('取消注销成功'));
      } catch (e) {
        tips.error(e as MsgError);
      }
      return cancelSuccess;
    },
    // 弹窗并选择是否取消注销
    async showCancelDeregiseterDialog(orgId: number, editionFlag: string, callback: Function) {
      // 企业版弹tips
      if (editionFlag === 'enterprise') {
        tips.info(i18n_t('当前身份已提交注销申请，请联系管理员进行处理'));
        return;
      }
      // 个人版、团队版弹弹窗
      const dialog: DialogInstance = CustomDialog.confirm({
        theme: 'warning',
        header: i18n_t('温馨提醒'),
        body: i18n_t('当前身份已提交注销申请，是否取消注销'),
        width: '400px',
        async onConfirm() {
          if (dialog.hide) {
            dialog.hide();
          }
          // 成功取消注销后再下一步
          tips.loading(i18n_t('正在取消注销'));
          const cancelSuccess = await methods.cancelDeregister(orgId);
          if (cancelSuccess) {
            tips.success(i18n_t('取消注销成功'));
            callback && callback();
          } else {
            tips.error(i18n_t('取消注销失败'));
          }
        },
      });
    },
    // 冻结提示框
    showFreezeDialog(editionFlag?: string) {
      const tips = editionFlag === 'personal' ? i18n_t('当前身份已被冻结') : i18n_t('当前身份已被冻结，请联系管理员');
      const dialog: DialogInstance = CustomDialog.alert({
        theme: 'warning',
        header: i18n_t('温馨提醒'),
        body: tips,
        destroyOnClose: true,
        closeOnOverlayClick: false,
        width: '500px',
        style: 'white-space: pre-line',
        async onConfirm() {
          if (dialog.hide) {
            dialog.hide();
          }
        },
        onClose: () => {
          if (dialog.hide) {
            dialog.hide();
          }
        },
      });
    },
    showIPRestrictDialog(isInvitation?: boolean) {
      const tips = isInvitation
        ? i18n_t('你已成功加入，但当前网络已被管理员设定为不可登录使用，需登录请联系管理员')
        : i18n_t('当前网络已被管理员设定为不可登录使用，需登录请联系管理员');
      const dialog: DialogInstance = CustomDialog.alert({
        theme: 'warning',
        header: i18n_t('限制登录提醒'),
        body: tips,
        destroyOnClose: true,
        closeOnOverlayClick: false,
        width: '400px',
        async onConfirm() {
          if (dialog.hide) {
            dialog.hide();
          }
        },
        onClose: () => {
          if (dialog.hide) {
            dialog.hide();
          }
        },
      });
    },
    // 创建渠道体验版
    async createOrgFn(channelTag?: SMH.ChannelTag) {
      creatingOrg.value = true;
      let createData = null;
      try {
        const { data } = await smh.userAPI.createChannelOrg({
          pf: channelTag,
        });
        // 默认企业版
        const editionFlag = data?.extensionData?.editionConfig?.editionFlag || 'enterprise';
        let editionType = '';
        if (editionFlag === 'enterprise') {
          editionType = i18n_t('企业版');
        } else if (editionFlag === 'team') {
          editionType = i18n_t('团队版');
        } else {
          editionType = i18n_t('个人版');
        }
        // 是否渠道体验版
        if (data?.extensionData?.channelFlag === 'meeting') {
          editionType = i18n_t('体验版');
        }
        createData = Object.assign(data, {
          deregister: data?.orgUser?.deregister || false,
          enabled: data?.orgUser?.enabled,
          editionFlag,
          editionType,
          channel: data?.extensionData?.channelFlag || '',
        });
        tips.success(i18n_t('创建成功'));
        user.setChannelApplied(true);
      } catch (e) {
        tips.error(e as MsgError);
      }
      creatingOrg.value = false;
      return createData;
    },
  };
  return {
    creatingOrg,
    ...methods,
  };
}
